import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Chip,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Check,
  Star,
  Rocket,
  AutoAwesome
} from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyId } from '../contexts/FamilyIdContext';
import { useNavigate } from 'react-router-dom';
import {
  getStripePublishableKey,
  SUBSCRIPTION_TIERS,
  getDaysRemainingInTrial,
  isSubscriptionActive
} from '../utils/stripeHelper';

const functions = getFunctions(app, 'us-central1');

function SubscriptionPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'annual'
  const { userProfile } = useAuth();
  const { familyData } = useFamilyId();
  const navigate = useNavigate();

  const isParent = userProfile?.role === 'parent';
  const daysRemaining = getDaysRemainingInTrial(familyData);
  const isActive = isSubscriptionActive(familyData);

  const handleSubscribe = async () => {
    if (!isParent) {
      setError('Only parents can manage subscriptions');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Initialize Stripe
      const stripeKey = getStripePublishableKey();
      if (!stripeKey) {
        throw new Error('Stripe configuration error');
      }
      const stripe = await loadStripe(stripeKey);

      // Call Cloud Function to create checkout session
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        familyId: familyData?.id,
        billingPeriod: billingPeriod // Pass the selected billing period
      });

      if (!result.data.sessionId) {
        throw new Error('Failed to create checkout session');
      }

      // Fire analytics event before redirect (best-effort)
      try {
        if (window.bsaTrack) {
          try {
            window.bsaTrack('cta_click', { metadata: { button: 'subscribe', plan: billingPeriod } });
          } catch (e) {
            console.log('Analytics tracking failed:', e);
          }
        }
      } catch (e) {}

      // Redirect to Stripe Checkout
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: result.data.sessionId
      });

      if (stripeError) {
        throw stripeError;
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to start subscription process');
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    setError('');

    try {
      // Call Cloud Function to create portal session
      const createPortalSession = httpsCallable(functions, 'createPortalSession');
      const result = await createPortalSession({
        familyId: familyData?.id
      });

      if (!result.data.url) {
        throw new Error('Failed to create portal session');
      }

      // Redirect to Stripe Customer Portal
      window.location.href = result.data.url;
    } catch (err) {
      console.error('Portal error:', err);
      setError(err.message || 'Failed to open subscription management');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #8E24AA 0%, #00BCD4 100%)',
        py: 4
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4, color: 'white' }}>
          <Box sx={{ fontSize: 80, mb: 2 }}>🤖✨</Box>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Earn-E Premium
          </Typography>
          <Typography variant="h6">
            Unlock the full power of family organization
          </Typography>
        </Box>

        {/* Trial Status */}
        {familyData?.subscriptionStatus === 'trialing' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600">
              🎉 You're on a free trial! {daysRemaining} days remaining
            </Typography>
            <Typography variant="body2">
              Subscribe now to continue enjoying all premium features after your trial ends.
            </Typography>
          </Alert>
        )}

        {/* Already Subscribed */}
        {familyData?.subscriptionStatus === 'active' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600">
              ✅ You're subscribed to Earn-E Premium!
            </Typography>
            <Typography variant="body2">
              Thank you for supporting Earn-E! Manage your subscription below.
            </Typography>
          </Alert>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Not Parent */}
        {!isParent && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1">
              Only parents can manage subscriptions. Please ask your parent to upgrade.
            </Typography>
          </Alert>
        )}

        {/* Pricing Card */}
        <Paper
          elevation={10}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            mb: 3
          }}
        >
          <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Chip
                icon={<Star />}
                label="MOST POPULAR"
                color="warning"
                sx={{ mb: 2, fontWeight: 'bold' }}
              />
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Premium Plan
              </Typography>
              {/* Billing Period Toggle */}
              <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                <ToggleButtonGroup
                  value={billingPeriod}
                  exclusive
                  onChange={(e, newPeriod) => newPeriod && setBillingPeriod(newPeriod)}
                  aria-label="billing period"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                >
                  <ToggleButton value="monthly" sx={{ color: 'white', fontWeight: 'bold', px: 3 }}>
                    Monthly
                  </ToggleButton>
                  <ToggleButton value="annual" sx={{ color: 'white', fontWeight: 'bold', px: 3 }}>
                    Annual
                    <Chip label="Save $20" color="warning" size="small" sx={{ ml: 1 }} />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ my: 3 }}>
                {billingPeriod === 'monthly' ? (
                  <>
                    <Typography variant="h2" fontWeight="bold" sx={{ display: 'inline' }}>
                      $9.99
                    </Typography>
                    <Typography variant="h6" sx={{ display: 'inline', ml: 1 }}>
                      /month
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                      Billed monthly
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h2" fontWeight="bold" sx={{ display: 'inline' }}>
                      $99
                    </Typography>
                    <Typography variant="h6" sx={{ display: 'inline', ml: 1 }}>
                      /year
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                      Save $20! Just $8.25/month
                    </Typography>
                  </>
                )}
              </Box>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                14-day free trial • Cancel anytime
              </Typography>
            </CardContent>
          </Card>

          <CardContent sx={{ py: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Everything you need to run your family:
            </Typography>
            <List>
              {SUBSCRIPTION_TIERS.premium.features.map((feature, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Check color="success" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>

            {/* CTA Button */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              {isActive ? (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleManageSubscription}
                  disabled={loading || !isParent}
                  startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesome />}
                  sx={{ py: 2, px: 4, fontSize: '1.1rem' }}
                >
                  {loading ? 'Loading...' : 'Manage Subscription'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubscribe}
                  disabled={loading || !isParent}
                  startIcon={loading ? <CircularProgress size={20} /> : <Rocket />}
                  sx={{
                    py: 2,
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(45deg, #4CAF50, #81C784)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #388E3C, #4CAF50)',
                    }
                  }}
                >
                  {loading ? 'Processing...' : 'Start Free Trial'}
                </Button>
              )}
            </Box>

            {/* Money Back */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', mt: 2 }}
            >
              💳 Secure payment by Stripe • Cancel anytime
            </Typography>
          </CardContent>
        </Paper>

        {/* Back Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={() => navigate(isParent ? '/parent' : '/child')}
            sx={{ color: 'white', fontWeight: 600 }}
          >
            ← Back to Dashboard
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default SubscriptionPage;

